import { apiFetch } from "./client";
import type {
  ImportRun,
  ImportType,
  InvoiceDocument,
  InvoiceDocumentStatus,
  InvoiceDocumentUpdate,
  Page,
  PdfExtractor,
} from "./types";
import type { ListParams } from "./customers";

export interface InvoiceDocumentFilters extends ListParams {
  status?: InvoiceDocumentStatus;
  match_status?: string;
}

/** List uploaded invoice documents, newest upload first. */
export async function listInvoiceDocuments(
  params: InvoiceDocumentFilters = {},
): Promise<Page<InvoiceDocument>> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  if (params.status) qs.set("status", params.status);
  if (params.match_status) qs.set("match_status", params.match_status);
  const query = qs.toString();
  return apiFetch(`/invoice-documents${query ? `?${query}` : ""}`);
}

export async function getInvoiceDocument(id: number): Promise<InvoiceDocument> {
  return apiFetch(`/invoice-documents/${id}`);
}

/** Apply manual corrections to an invoice and re-match it to a load / rate card. */
export async function updateInvoiceDocument(
  id: number,
  changes: InvoiceDocumentUpdate,
): Promise<InvoiceDocument> {
  return apiFetch(`/invoice-documents/${id}`, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });
}

/** Record an Accept / Query / Reject decision on an invoice document. */
export async function decideInvoiceDocument(
  id: number,
  status: InvoiceDocumentStatus,
  note?: string | null,
): Promise<InvoiceDocument> {
  return apiFetch(`/invoice-documents/${id}/decision`, {
    method: "POST",
    body: JSON.stringify({ status, note: note ?? null }),
  });
}

/** Upload an invoice PDF through the import pipeline (extract + match + check). */
export async function uploadInvoicePdf(file: File): Promise<ImportRun> {
  const body = new FormData();
  body.append("file", file);
  return apiFetch("/import-types/invoice/import-pdf", {
    method: "POST",
    body,
  });
}

// --- PDF field extractors (managed in the Imports → PDF extractors tab) -----

export interface PdfExtractorInput {
  import_type: ImportType;
  destination_field: string;
  label?: string | null;
  pattern?: string | null;
  is_required?: boolean;
  sort_order?: number;
}

export async function listPdfExtractors(importType: ImportType): Promise<PdfExtractor[]> {
  return apiFetch(`/pdf-extractors?import_type=${importType}`);
}

export async function createPdfExtractor(body: PdfExtractorInput): Promise<PdfExtractor> {
  return apiFetch("/pdf-extractors", { method: "POST", body: JSON.stringify(body) });
}

export async function updatePdfExtractor(
  id: number,
  body: Partial<PdfExtractorInput>,
): Promise<PdfExtractor> {
  return apiFetch(`/pdf-extractors/${id}`, { method: "PUT", body: JSON.stringify(body) });
}

export async function deletePdfExtractor(id: number): Promise<void> {
  return apiFetch(`/pdf-extractors/${id}`, { method: "DELETE" });
}
